import React, { useEffect, FC, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validateLoanTerm } from '@qred/shared-component-library/src/validators';
import {
  getDefaultValue,
  dropDownOptions,
  createButtons,
  getSliderValues,
} from './LoanTermService';
import { IBaseComponent } from '~/sharedInterfaces/IBaseComponent';
import { setInvalidError, setMissingError } from '~/actions/errorActions';
import { setInput, validateValue } from '~/actions/formActions';
import { LOAN_TERM } from '~/actions/actionTypes';
import DropDown from '../Base/DropDown/DropDown';
import Input from '../Base/Input/Input';
import Slider from '../Base/Slider/Slider';
import SelectButtons from '../Base/SelectButtons/SelectButtons';
import { IState } from '~/sharedInterfaces/IState';
import {
  IBaseInput,
  IDropDownOption,
  ISelectButtons,
  ISliderValues,
  ITermsText,
} from '~/sharedInterfaces/SharedInterfaces';
import { getDefaultLabelName } from '~/services/DefaultLabelNameService';

enum ComponentTypes {
  DROPDOWN = 'dropDown',
  SLIDER = 'slider',
  SELECT_BUTTONS = 'selectButtons',
}

export interface ILoanTerm extends IBaseInput {
  componentType?: ComponentTypes;
  termsText?: ITermsText;
  sliderValues?: ISliderValues;
  buttons?: ISelectButtons[];
  dropDownValues?: IDropDownOption[];
}

interface ILoanTermProps extends ILoanTerm, IBaseComponent {}

const LoanTerm: FC<ILoanTermProps> = (props) => {
  const dispatch = useDispatch();
  const { market } = props.settings;

  const value = useSelector((state: IState) => state.signup.loanTerm || 0);
  const error = useSelector((state: IState) => state.error.loanTerm);
  const { locale } = useSelector((state: IState) => state.widgetContext);
  const sliderValues = getSliderValues(props.sliderValues, locale);
  const { unit } = getDefaultLabelName(locale, LOAN_TERM);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    dispatch(setInput(props.componentName, inputValue));
    if (error?.missing) dispatch(setMissingError(LOAN_TERM, ''));
  };

  const handleValidation = () => {
    dispatch(validateValue(validateLoanTerm, LOAN_TERM, value, locale));
  };

  useEffect(() => {
    const validLoanTerm = validateLoanTerm(value);
    if (validLoanTerm && error?.invalid) dispatch(setInvalidError(LOAN_TERM, ''));
  }, [value]);

  const defaultProps = {
    ...props,
    value: value ? value.toString() : '',
    error: error?.missing || error?.invalid,
    formatValueAmount: `${value}`,
    formatValueUnit: `${sliderValues.unit}`,
    onChange: handleOnChange,
    analyticsEvent: 'loan-term',
    handleValidation,
  };

  useEffect(() => {
    if (
      !defaultProps.value &&
      (props.componentType === ComponentTypes.SELECT_BUTTONS ||
        props.componentType === ComponentTypes.DROPDOWN ||
        props.componentType === ComponentTypes.SLIDER)
    ) {
      dispatch(setInput(props.componentName, getDefaultValue(market).toString()));
    }
  }, []);

  switch (props.componentType) {
    case ComponentTypes.SELECT_BUTTONS:
      return (
        <SelectButtons
          {...defaultProps}
          value={value}
          buttons={props.buttons || createButtons(market, locale)}
        />
      );
    case ComponentTypes.DROPDOWN:
      return (
        <DropDown
          {...defaultProps}
          dropDownValues={props.dropDownValues || dropDownOptions(market, locale)}
        />
      );
    case ComponentTypes.SLIDER:
      return <Slider {...defaultProps} sliderValues={sliderValues} />;
    default:
      return (
        <Input {...defaultProps} inputMode="numeric">
          <p className="qred-first">
            <span className="qred-first">{unit}</span>
          </p>
        </Input>
      );
  }
};

export default LoanTerm;
